import React, { useState, useEffect } from 'react';
import styles from './CheckoutModal.module.css';
import { useCart } from '../../context/CartContext';
import { apiService } from '../../services/api';

const CheckoutModal = ({ open, onClose, directProduct }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const { cartItems } = useCart();

  // Si es compra directa, no agregar al carrito, solo procesar el producto recibido
  const productsToBuy = React.useMemo(() => directProduct ? [directProduct] : cartItems, [directProduct, cartItems]);
  const isCart = !directProduct; // Es del carrito si NO hay producto directo

  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });

  useEffect(() => {
    const guestEmail = localStorage.getItem('guest_email');
    if (guestEmail) setForm(f => ({ ...f, email: guestEmail }));
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const total = productsToBuy.reduce((sum, item) => sum + (item.price || 0) * (item.quantity || 1), 0);

  // Resumen interactivo de productos
  const renderProductSummary = () => (
    <div className={styles.productSummaryBox}>
      <h4 className={styles.productSummaryTitle}>Estás pagando por estos productos:</h4>
      <ul className={styles.productSummaryList}>
        {productsToBuy.map(item => (
          <li key={item.id} className={styles.productSummaryItem}>
            <span className={styles.productSummaryName}>{item.name}</span>
            <span className={styles.productSummaryQty}>x{item.quantity || 1}</span>
            <span className={styles.productSummaryPrice}>${(item.price || 0).toFixed(2)}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const handleSubmit = async e => {
    e.preventDefault();
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = 'El nombre es obligatorio.';
    if (!form.phone.trim() || !/^\d{7,15}$/.test(form.phone.trim())) newErrors.phone = 'Teléfono inválido.';
    if (!form.address.trim()) newErrors.address = 'La dirección es obligatoria.';
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;
    setLoading(true);
    try {
      const data = await apiService.createOrder({
        ...form,
        products: productsToBuy,
        isFromCart: isCart // Indicar si viene del carrito
      });
      if (data.paymentUrl) {
        window.location.href = data.paymentUrl;
      } else if (data.urlEnlace) {
        window.location.href = data.urlEnlace;
      } else if (data.urlEnlaceLargo) {
        window.location.href = data.urlEnlaceLargo;
      } else {
        alert('No se recibió la URL de pago.');
      }
    } catch (err) {
      alert('Error al procesar la orden');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.customHeader}>
          <img src="/Nexa-Globals-Hlogo-removebg-preview.png" alt="Logo" className={styles.logo} />
          <button className={styles.closeButton} onClick={onClose}>
            X
          </button>
        </div>
        <div className={styles.checkoutGrid}>
          <div className={styles.leftColumn}>
            <div className={styles.formSection}>
              <form className={styles.form} onSubmit={handleSubmit}>
                <div className={styles.row}>
                  <label>
                    Nombre:
                    <input name="name" value={form.name} onChange={handleChange} required />
                    {errors.name && <span className={styles.error}>{errors.name}</span>}
                  </label>
                  <label>
                    Teléfono:
                    <input name="phone" value={form.phone} onChange={handleChange} required />
                    {errors.phone && <span className={styles.error}>{errors.phone}</span>}
                  </label>
                </div>
                <div className={styles.row}>
                  <label>
                    Dirección:
                    <input name="address" value={form.address} onChange={handleChange} required />
                    {errors.address && <span className={styles.error}>{errors.address}</span>}
                  </label>
                  <label>
                    Correo electrónico:
                    <input name="email" value={form.email} onChange={handleChange} required />
                  </label>
                </div>
                <button
                  type="submit"
                  className={styles.payButton}
                  disabled={loading}
                >
                  {loading ? 'Procesando...' : 'Pagar con Wompi'}
                </button>
              </form>
            </div>
            <div className={styles.productsSection}>
              <h3>Productos</h3>
              <div className={styles.productsList}>
                {productsToBuy.map(item => (
                  <div key={item.id} className={styles.productItem}>
                    <img
                      src={
                        Array.isArray(item.product_images)
                          ? (item.product_images.find(img => img.is_primary)?.cloudflare_url || item.product_images[0]?.cloudflare_url || '/placeholder-image.jpg')
                          : (item.primary_image || item.image || '/placeholder-image.jpg')
                      }
                      alt={item.name}
                      className={styles.productImage}
                    />
                    <div className={styles.productInfo}>
                      <div className={styles.productName}>{item.name}</div>
                      <div className={styles.productQty}>Cantidad: {item.quantity || 1}</div>
                    </div>
                  </div>
                ))}
              </div>
              {productsToBuy.length > 1 && renderProductSummary()}
            </div>
          </div>
          <div className={styles.rightColumn}>
            <div className={styles.summaryBox}>
              <h3>Resumen</h3>
              <div className={styles.summaryRow}>
                <span>Subtotal:</span>
                <span>${total.toFixed(2)}</span>
              </div>
              <div className={styles.summaryRow}>
                <span>Envío:</span>
                <span>$0.00</span>
              </div>
              <div className={styles.summaryRowTotal}>
                <span>Total:</span>
                <span>${total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
